import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { PublicNavbar, PublicFooter } from "@/components/layout/PublicNav";
import { toast } from "sonner";
import { Sparkles, Send } from "lucide-react";

export default function PublicTeacherProfile() {
  const { slug } = useParams();
  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", preferred_time: "" });
  const [submitting, setSubmitting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/public/teachers/${slug}`); setTeacher(data.teacher); }
      catch { toast.error("Teacher not found"); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  useEffect(() => {
    if (teacher) {
      document.title = `${teacher.full_name} — ${teacher.subjects?.[0] || "Online tutor"} | TeachingSoftware.ai`;
      // Description meta
      let metaDesc = document.querySelector('meta[name="description"]');
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute(
        "content",
        `${teacher.full_name} teaches ${(teacher.subjects || []).join(", ")} for ${(teacher.grades || []).join(", ")}. ${teacher.experience_years || 0}+ years of online tutoring experience. Book a free demo class.`
      );
      // JSON-LD structured data (Person + EducationalOrganization)
      const jsonld = {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Person",
            name: teacher.full_name,
            description: teacher.bio || "",
            jobTitle: `Online ${(teacher.subjects || [])[0] || "subject"} tutor`,
            knowsAbout: teacher.subjects || [],
            url: typeof window !== "undefined" ? window.location.href : "",
            image: teacher.avatar_url || undefined,
            address: teacher.country ? { "@type": "PostalAddress", addressCountry: teacher.country } : undefined,
            worksFor: { "@type": "Organization", name: "TeachingSoftware.ai" },
          },
          {
            "@type": "EducationalOccupationalProgram",
            name: `Online tutoring with ${teacher.full_name}`,
            provider: { "@type": "Person", name: teacher.full_name },
            educationalLevel: (teacher.grades || []).join(", "),
            programType: "tutoring",
            offers: { "@type": "Offer", availability: "https://schema.org/InStock", price: "0", priceCurrency: "USD", description: "Free demo class" },
          },
        ],
      };
      let script = document.getElementById("teacher-jsonld");
      if (!script) {
        script = document.createElement("script");
        script.id = "teacher-jsonld";
        script.type = "application/ld+json";
        document.head.appendChild(script);
      }
      script.textContent = JSON.stringify(jsonld);
    }
    return () => {
      const s = document.getElementById("teacher-jsonld");
      if (s) s.remove();
    };
  }, [teacher]);

  const book = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post(`/public/teachers/${slug}/demo-booking`, form);
      toast.success("Demo request sent!");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", preferred_time: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!teacher) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Teacher not found.</div>;

  return (
    <div>
      <PublicNavbar />
      <section className="bg-[#0A0C10] text-white py-16 noise-overlay">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-400 mb-3">/ verified tutor profile</div>
            <h1 className="font-display font-bold text-4xl lg:text-6xl tracking-tighter">{teacher.full_name}</h1>
            <p className="mt-3 text-neutral-300 text-lg">{(teacher.subjects || []).join(" · ") || "Online tutor"} · {teacher.country || "Online"}</p>
            <p className="mt-6 max-w-2xl text-neutral-300">{teacher.bio || "Passionate online educator helping students grow."}</p>
            <dl className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 max-w-2xl">
              <Stat label="Experience" value={`${teacher.experience_years || 0}+ yrs`} />
              <Stat label="Language" value={teacher.teaching_language || "English"} />
              <Stat label="Grades" value={(teacher.grades || []).join(", ") || "All"} />
              <Stat label="Available" value={teacher.availability || "Flexible"} />
            </dl>
          </div>
          <div className="lg:col-span-1">
            <div className="bg-white text-[#0A0C10] p-6">
              <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500">Book a demo class</div>
              <form onSubmit={book} className="mt-3 space-y-2">
                <input className={inp} required placeholder="Your name *" value={form.name} onChange={set("name")} data-testid="demo-name" />
                <input className={inp} type="email" required placeholder="Email *" value={form.email} onChange={set("email")} data-testid="demo-email" />
                <input className={inp} placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} />
                <input className={inp} placeholder="Subject" value={form.subject} onChange={set("subject")} />
                <input className={inp} placeholder="Preferred time" value={form.preferred_time} onChange={set("preferred_time")} />
                <textarea className={inp} rows={2} placeholder="Your message" value={form.message} onChange={set("message")} />
                <button type="submit" disabled={submitting} data-testid="demo-submit" className="w-full bg-[#FFD600] text-[#0A0C10] font-bold py-3 btn-brutal-shadow disabled:opacity-60">
                  <Send size={14} className="inline" /> {submitting ? "Sending…" : "Request demo class"}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tighter">What I teach</h2>
            <ul className="mt-4 space-y-1.5 text-neutral-700 text-sm">
              {(teacher.subjects || []).length === 0 ? <li>Various subjects</li> : teacher.subjects.map((s) => <li key={s}>· {s}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tighter">Class levels</h2>
            <ul className="mt-4 space-y-1.5 text-neutral-700 text-sm">
              {(teacher.grades || []).length === 0 ? <li>All grades</li> : teacher.grades.map((g) => <li key={g}>· {g}</li>)}
            </ul>
          </div>
          <div>
            <h2 className="font-display font-bold text-2xl tracking-tighter">My approach</h2>
            <p className="mt-4 text-sm text-neutral-700">I plan every class with AI-assisted lesson plans, run interactive live sessions with instant quizzes, and share a post-class report so you know exactly what to revise. <Sparkles size={12} className="inline text-[#FFD600]" /></p>
          </div>
        </div>
      </section>
      <PublicFooter />
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#0A0C10] p-4">
      <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-mono">{label}</div>
      <div className="text-lg font-display font-bold mt-2">{value}</div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-3 py-2 text-sm focus:ring-2 focus:ring-[#002FA7] outline-none";
